import { useMsal } from '@azure/msal-react';
import {
  Button,
  Card,
  Spinner,
  Text,
  Title2,
  Badge,
  tokens,
} from '@fluentui/react-components';
import { useCallback, useState } from 'react';

import { sigsRequest } from '../../auth/sigsMsalConfig';

interface SigsSignalResponse {
  value?: unknown[];
  '@odata.nextLink'?: string;
  '@odata.context'?: string;
}

interface SigsTestResult {
  status: 'idle' | 'loading' | 'success' | 'error';
  tokenAcquired: boolean;
  signalCount: number;
  error?: string;
  rawData?: unknown;
}

async function fetchSigsSignals(
  accessToken: string,
): Promise<SigsSignalResponse> {
  const now = new Date();
  const sixHoursAgo = new Date(now.getTime() - 6 * 60 * 60 * 1000);
  const filterExpression = `signaltype eq 'LlmPromptResponse' and starttime ge ${sixHoursAgo.toISOString()} and endtime le ${now.toISOString()}`;
  const url = `https://substrate.office.com/api/v2.0/me/signals?filter=${encodeURIComponent(filterExpression)}&orderby=signaltype,starttime,endtime`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/json',
    },
    cache: 'no-store',
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(
      `HTTP ${response.status.toString()}: ${errorText.slice(0, 500)}`,
    );
  }

  return response.json() as Promise<SigsSignalResponse>;
}

export function SigsTestPage() {
  const { instance, accounts } = useMsal();
  const [result, setResult] = useState<SigsTestResult>({
    status: 'idle',
    tokenAcquired: false,
    signalCount: 0,
  });

  const testSigsCall = useCallback(() => {
    const run = async () => {
      setResult({ status: 'loading', tokenAcquired: false, signalCount: 0 });

      let accessToken: string | undefined;

      // Step 1: Acquire token (silent first, then popup)
      try {
        const tokenResponse = await instance.acquireTokenSilent({
          ...sigsRequest,
          account: accounts[0],
        });
        accessToken = tokenResponse.accessToken;
      } catch {
        // Silent failed — try interactive popup
        try {
          const popupResponse = await instance.acquireTokenPopup({
            ...sigsRequest,
            account: accounts[0],
          });
          accessToken = popupResponse.accessToken;
        } catch (popupErr) {
          setResult({
            status: 'error',
            tokenAcquired: false,
            signalCount: 0,
            error: `Token acquisition failed: ${popupErr instanceof Error ? popupErr.message : String(popupErr)}`,
          });
          return;
        }
      }

      if (!accessToken) {
        setResult({
          status: 'error',
          tokenAcquired: false,
          signalCount: 0,
          error: 'No access token received',
        });
        return;
      }

      // Step 2: Call Substrate SIGS API
      try {
        const data = await fetchSigsSignals(accessToken);
        const signals = data.value ?? [];

        setResult({
          status: 'success',
          tokenAcquired: true,
          signalCount: signals.length,
          rawData: data,
        });
      } catch (fetchErr) {
        setResult({
          status: 'error',
          tokenAcquired: true,
          signalCount: 0,
          error:
            fetchErr instanceof Error ? fetchErr.message : String(fetchErr),
        });
      }
    };

    void run();
  }, [instance, accounts]);

  return (
    <div style={{ padding: '2rem', maxWidth: '900px' }}>
      <Title2>SIGS API Test</Title2>
      <Text
        style={{
          display: 'block',
          marginTop: '0.5rem',
          color: tokens.colorNeutralForeground3,
        }}
      >
        Test whether your app can call the Substrate SIGS API to fetch your
        recent LlmPromptResponse signals.
      </Text>

      <Card style={{ marginTop: '1.5rem', padding: '1.5rem' }}>
        <Button
          appearance="primary"
          onClick={testSigsCall}
          disabled={result.status === 'loading'}
        >
          {result.status === 'loading' ? 'Testing...' : 'Test SIGS API Call'}
        </Button>

        {result.status === 'loading' && (
          <div style={{ marginTop: '1rem' }}>
            <Spinner
              size="small"
              label="Acquiring token and calling SIGS API..."
            />
          </div>
        )}

        {result.status === 'success' && (
          <div style={{ marginTop: '1rem' }}>
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                marginBottom: '1rem',
              }}
            >
              <Badge appearance="filled" color="success">
                Token Acquired
              </Badge>
              <Badge appearance="filled" color="success">
                API Call Successful
              </Badge>
              <Badge appearance="filled" color="informative">
                {result.signalCount} signals found
              </Badge>
            </div>
            <Text style={{ display: 'block', marginBottom: '0.5rem' }}>
              Found {result.signalCount} LlmPromptResponse signals in the last 6
              hours.
            </Text>
            {result.rawData != null && (
              <details>
                <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                  View Raw Response
                </summary>
                <pre
                  style={{
                    marginTop: '0.5rem',
                    padding: '1rem',
                    backgroundColor: '#1e1e1e',
                    color: '#d4d4d4',
                    borderRadius: '4px',
                    overflow: 'auto',
                    maxHeight: '400px',
                    fontSize: '12px',
                  }}
                >
                  {JSON.stringify(result.rawData, null, 2)}
                </pre>
              </details>
            )}
          </div>
        )}

        {result.status === 'error' && (
          <div style={{ marginTop: '1rem' }}>
            <div
              style={{
                display: 'flex',
                gap: '1rem',
                alignItems: 'center',
                marginBottom: '1rem',
              }}
            >
              {result.tokenAcquired ? (
                <Badge appearance="filled" color="success">
                  Token Acquired
                </Badge>
              ) : (
                <Badge appearance="filled" color="danger">
                  Token Failed
                </Badge>
              )}
              <Badge appearance="filled" color="danger">
                API Call Failed
              </Badge>
            </div>
            <pre
              style={{
                padding: '1rem',
                backgroundColor: tokens.colorPaletteRedBackground1,
                color: tokens.colorPaletteRedForeground1,
                borderRadius: '4px',
                overflow: 'auto',
                maxHeight: '300px',
                fontSize: '12px',
                whiteSpace: 'pre-wrap',
              }}
            >
              {result.error}
            </pre>
          </div>
        )}
      </Card>
    </div>
  );
}
